export function createMappedClickActionHandler(deps) {
  const isTopLevelTab = (tab) => ["home", "logs", "programs", "analytics", "profile"].includes(tab);

  return async function handleMappedClickAction(target) {
    if (!(target instanceof HTMLElement)) {
      return false;
    }

    const action = String(target.dataset.action || "").trim();
    if (!action) {
      return false;
    }

    if (action === "app.select-tab") {
      const nextTab = String(target.dataset.tab || "").trim();
      if (!nextTab) {
        return false;
      }

      const currentState = deps.getState();
      const currentTab = String(currentState.selectedTab || "home").trim() || "home";
      const currentTask = deps.captureCurrentAppTask();
      if (nextTab === currentTab && !currentTask) {
        return true;
      }

      if (currentTask?.type === "program-builder" && nextTab !== "programs") {
        await deps.confirmProgramBuilderExit(() => {
          const finalizeExit = () => {
            deps.handleCancelProgramBuilder();
            if (nextTab === "logs") {
              deps.openLogsTab({ syncHistory: false });
            } else {
              deps.setState({ selectedTab: nextTab });
            }
            deps.replaceCurrentAppHistorySnapshot({ force: true });
            deps.setAppHistoryAutoReplaceSuspended(false);
          };
          deps.setAppHistoryAutoReplaceSuspended(true);
          if (!deps.consumeCurrentAppHistoryEntry({ onConsumed: finalizeExit })) {
            finalizeExit();
          }
        });
        return true;
      }

      if (currentTask) {
        if (nextTab === currentTab) {
          return true;
        }
        if (nextTab === "home") {
          deps.resetAppNavigationToHome();
          return true;
        }
        deps.resetAppNavigationToHome(() => {
          deps.openTopLevelTabFromRoot(nextTab);
        });
        return true;
      }

      if (nextTab === "home" && currentTab !== "home") {
        if (!deps.consumeCurrentAppHistoryEntry({ applySnapshot: true })) {
          deps.setState({ selectedTab: "home" });
        }
        return true;
      }

      const shouldReplaceTopLevel =
        isTopLevelTab(currentTab) &&
        isTopLevelTab(nextTab) &&
        currentTab !== "home" &&
        nextTab !== "home";

      if (nextTab === "logs") {
        deps.openLogsTab({
          syncHistory: !shouldReplaceTopLevel,
        });
        if (shouldReplaceTopLevel) {
          deps.replaceCurrentAppHistorySnapshot();
        }
        return true;
      }

      if (nextTab === "programs") {
        deps.setAppHistoryAutoReplaceSuspended(true);
        try {
          deps.setState({
            selectedTab: nextTab,
            programBuilderStage: "list",
          });
          if (shouldReplaceTopLevel || nextTab === "home") {
            deps.replaceCurrentAppHistorySnapshot({ force: true });
          } else {
            deps.pushCurrentAppHistorySnapshot();
          }
        } finally {
          deps.setAppHistoryAutoReplaceSuspended(false);
        }
        return true;
      }

      deps.setAppHistoryAutoReplaceSuspended(true);
      try {
        deps.setState({ selectedTab: nextTab });
        if (shouldReplaceTopLevel || nextTab === "home") {
          deps.replaceCurrentAppHistorySnapshot({ force: true });
        } else {
          deps.pushCurrentAppHistorySnapshot();
        }
      } finally {
        deps.setAppHistoryAutoReplaceSuspended(false);
      }
      return true;
    }

    if (action === "analytics.select-exercise") {
      await deps.handleSelectExercise(target.dataset.exerciseId, target.dataset.exerciseName);
      if (deps.isAnalyticsSearchModalOpen()) {
        deps.closeAnalyticsSearchModal();
      }
      return true;
    }

    if (action === "logs.select-exercise") {
      await deps.handleSelectLogsExercise(target.dataset.exerciseId, target.dataset.exerciseName);
      if (deps.isAnalyticsSearchModalOpen()) {
        deps.closeAnalyticsSearchModal();
      }
      return true;
    }

    if (action === "programs.select-item-exercise") {
      // dayIndex/itemIndex는 버튼이 아닌 모달 dataset에서 읽음 (extraAttributes 미사용)
      const searchModal = document.getElementById("analyticsSearchModal");
      const dayIndex = Number(target.dataset.dayIndex ?? searchModal?.dataset.dayIndex);
      const itemIndex = Number(target.dataset.itemIndex ?? searchModal?.dataset.itemIndex);
      deps.handleSelectProgramItemExercise(
        dayIndex,
        itemIndex,
        target.dataset.exerciseId,
        target.dataset.exerciseName,
      );
      if (deps.isAnalyticsSearchModalOpen()) {
        deps.closeAnalyticsSearchModal();
      }
      return true;
    }

    if (action === "session.select-add-exercise") {
      deps.handleSelectSessionAddExercise(target.dataset.exerciseId, target.dataset.exerciseName);
      return true;
    }

    return false;
  };
}
